import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { SHOWROOM } from "@/constants/testIds";

export const BuildForm = ({ spa, shellName, cabinetName, trimName, renderUrl, onClose }) => {
  const [form, setForm] = useState({
    first_name: "", surname: "", suburb: "", postcode: "", contact_number: "", email: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const combo = [shellName, cabinetName, trimName].filter(Boolean).join(" · ");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.surname.trim()) return toast.error("Please enter your first name and surname");
    if (!form.email.trim()) return toast.error("Please enter your email address");
    setLoading(true);
    try {
      await api.post("/build-enquiry", {
        spa_id: spa?.id || null,
        spa_name: spa?.name || "",
        shell: shellName || "", cabinet: cabinetName || "", trim: trimName || "",
        render_url: renderUrl ? mediaUrl(renderUrl) : "",
        first_name: form.first_name, surname: form.surname,
        suburb: form.suburb, postcode: form.postcode,
        contact_number: form.contact_number, email: form.email, message: form.message,
      });
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
        <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-[#1d1f26]/95 p-6 backdrop-blur-xl max-h-[90vh] overflow-y-auto"
          initial={{ y: 24, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 24, opacity: 0 }} transition={{ type: "spring", stiffness: 260, damping: 24 }}
          data-testid={SHOWROOM.buildForm}
        >
          <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-white" data-testid="build-form-close">
            <X size={20} />
          </button>

          {done ? (
            <div className="py-8 text-center" data-testid={SHOWROOM.buildSuccess}>
              <CheckCircle2 className="mx-auto mb-4 text-cyan-400" size={48} />
              <h3 className="font-display text-2xl font-semibold tracking-tight">Build sent!</h3>
              <p className="mt-2 text-sm text-slate-400">
                Thanks {form.first_name} — we've emailed a copy of your {spa?.name} build to you, and our team will be in touch shortly.
              </p>
              <Button onClick={onClose} className="mt-6 rounded-full bg-cyan-500 px-6 text-black hover:bg-cyan-400">Back to showroom</Button>
            </div>
          ) : (
            <>
              <span className="text-xs uppercase tracking-widest text-cyan-400">{spa?.name}</span>
              <h3 className="mt-1 font-display text-2xl font-semibold tracking-tight">Save my build</h3>
              <p className="mt-1 text-sm text-slate-400">We'll email your colour combo to you and our sales team.</p>

              {renderUrl ? (
                <div className="mt-4 overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] p-2">
                  <img src={mediaUrl(renderUrl)} alt="Your build" className="mx-auto h-36 w-auto object-contain" data-testid="build-render-preview" />
                  <p className="pb-1 text-center text-xs text-amber-200">{combo}</p>
                </div>
              ) : null}

              <form onSubmit={submit} className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="First name" value={form.first_name} onChange={set("first_name")} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={SHOWROOM.buildFirstName} />
                  <Input placeholder="Surname" value={form.surname} onChange={set("surname")} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={SHOWROOM.buildSurname} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Suburb" value={form.suburb} onChange={set("suburb")} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={SHOWROOM.buildSuburb} />
                  <Input placeholder="Postcode" value={form.postcode} onChange={set("postcode")} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={SHOWROOM.buildPostcode} />
                </div>
                <Input placeholder="Contact number" value={form.contact_number} onChange={set("contact_number")} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={SHOWROOM.buildPhone} />
                <Input type="email" placeholder="Email address" value={form.email} onChange={set("email")} className="border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={SHOWROOM.buildEmail} />
                <Textarea placeholder="Message (optional)" value={form.message} onChange={set("message")} className="min-h-[70px] border-white/10 bg-white/5 focus-visible:ring-cyan-500" data-testid={SHOWROOM.buildMessage} />
                <Button type="submit" disabled={loading} className="w-full rounded-full bg-cyan-500 py-6 text-base font-semibold text-black hover:bg-cyan-400" data-testid={SHOWROOM.buildSubmit}>
                  {loading ? <Loader2 className="animate-spin" size={18} /> : "Email me my build"}
                </Button>
              </form>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
